import React from "react";
import { KeyRound } from "lucide-react";
import type { LaunchableParameter } from "server";
import { SectionShell } from "../layout";
import type { SectionControlProps } from "../types";
import {
  LaunchableParameterDefinitionEditor,
  normalizeLaunchableParameters,
} from "../../shared/LaunchableParameters";

const ParameterPreview: React.FC<{ parameters: LaunchableParameter[] }> = ({
  parameters,
}) => {
  const normalizedParameters = normalizeLaunchableParameters(parameters);

  if (normalizedParameters.length === 0) return null;

  return (
    <ul className="mt-3 flex flex-wrap gap-2">
      {normalizedParameters.map((parameter, index) => (
        <li
          key={`${parameter.name}-${index}`}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-xs text-secondary"
        >
          <span className="font-mono">{parameter.name || "Unnamed"}</span>
          <span>
            ({parameter.choice ? "choice" : "text"},{" "}
            {parameter.required ? "required" : "optional"})
          </span>
        </li>
      ))}
    </ul>
  );
};

export const ParametersSection: React.FC<
  SectionControlProps & {
    parameters: LaunchableParameter[];
    hasError: boolean;
    showErrors: boolean;
    loadingCreatingLaunchable: boolean;
    onParametersChange: (parameters: LaunchableParameter[]) => void;
  }
> = ({
  parameters,
  hasError,
  showErrors,
  loadingCreatingLaunchable,
  onParametersChange,
  status,
  ...sectionProps
}) => (
  <SectionShell
    id="parameters"
    title="Launch parameters"
    summary={
      parameters.length === 0
        ? "No launch parameters configured"
        : `${parameters.length} launch ${
            parameters.length === 1 ? "parameter" : "parameters"
          }`
    }
    collapsedPreview={<ParameterPreview parameters={parameters} />}
    icon={<KeyRound className="h-5 w-5" />}
    status={hasError && showErrors ? "error" : status}
    {...sectionProps}
  >
    <LaunchableParameterDefinitionEditor
      parameters={parameters}
      onChange={onParametersChange}
      disabled={loadingCreatingLaunchable}
      showErrors={showErrors}
    />
  </SectionShell>
);
